export type NotificationTemplateId =
  | 'tender-letter-success'
  | 'tender-letter-error'
  | 'boq-export-success'
  | 'boq-export-error'
  | 'folder-permission-load-error'
  | 'folder-permission-missing-email'
  | 'folder-permission-invalid-email'
  | 'folder-permission-duplicate'
  | 'folder-permission-granted'
  | 'folder-permission-removed'
  | 'folder-permission-updated'
  | 'folder-permission-grant-error'
  | 'folder-permission-remove-error'
  | 'folder-permission-update-error'
  | 'device-push-register-success'
  | 'device-push-register-error'
  | 'device-push-remove-success'
  | 'device-push-remove-error'
  | 'device-push-test-success'
  | 'device-push-test-warning'
  | 'drive-connected'
  | 'xero-connected'
  | 'xero-connection-failed'
  | 'xero-disconnected'
  | 'xero-disconnect-failed'
  | 'xero-sync-success'
  | 'xero-sync-error';

type Template = {
  type: 'success' | 'error' | 'info' | 'warning';
  title: string;
  message: string;
};

type Context = Record<string, string | number | undefined>;

const templates: Record<NotificationTemplateId, (ctx: Context) => Template> = {
  'tender-letter-success': (ctx) => ({
    type: 'success',
    title: 'Tender letter ready',
    message: ctx.name
      ? `${ctx.name} is ready to view in Google Docs.`
      : 'Tender letter is ready to view in Google Docs.',
  }),
  'tender-letter-error': (ctx) => ({
    type: 'error',
    title: 'Tender letter failed',
    message: ctx.reason ? String(ctx.reason) : 'We could not generate the tender letter.',
  }),
  'boq-export-success': (ctx) => ({
    type: 'success',
    title: 'BOQ exported',
    message: ctx.filename ? `${ctx.filename} saved.` : 'BOQ export completed.',
  }),
  'boq-export-error': (ctx) => ({
    type: 'error',
    title: 'BOQ export failed',
    message: ctx.reason ? String(ctx.reason) : 'We could not export the BOQ.',
  }),
  'folder-permission-load-error': () => ({
    type: 'error',
    title: 'Permissions unavailable',
    message: 'We could not load folder permissions. Please try again.',
  }),
  'folder-permission-missing-email': () => ({
    type: 'warning',
    title: 'Email required',
    message: 'Enter an email address to grant access.',
  }),
  'folder-permission-invalid-email': () => ({
    type: 'warning',
    title: 'Invalid email',
    message: 'That email address looks invalid. Please check and try again.',
  }),
  'folder-permission-duplicate': () => ({
    type: 'warning',
    title: 'User already has access',
    message: 'This person already has access to the folder.',
  }),
  'folder-permission-granted': (ctx) => ({
    type: 'success',
    title: 'Access granted',
    message: ctx.email ? `${ctx.email} now has access.` : 'Access granted.',
  }),
  'folder-permission-removed': (ctx) => ({
    type: 'success',
    title: 'Access removed',
    message: ctx.email ? `${ctx.email} no longer has access.` : 'Access removed.',
  }),
  'folder-permission-updated': (ctx) => ({
    type: 'success',
    title: 'Permission updated',
    message: ctx.email ? `${ctx.email} permissions updated.` : 'Permission updated.',
  }),
  'folder-permission-grant-error': (ctx) => ({
    type: 'error',
    title: 'Could not grant access',
    message: ctx.reason ? String(ctx.reason) : 'Please try again.',
  }),
  'folder-permission-remove-error': (ctx) => ({
    type: 'error',
    title: 'Could not remove access',
    message: ctx.reason ? String(ctx.reason) : 'Please try again.',
  }),
  'folder-permission-update-error': (ctx) => ({
    type: 'error',
    title: 'Could not update permission',
    message: ctx.reason ? String(ctx.reason) : 'Please try again.',
  }),
  'device-push-register-success': () => ({
    type: 'success',
    title: 'Device registered',
    message: 'Push notifications enabled on this device.',
  }),
  'device-push-register-error': (ctx) => ({
    type: 'error',
    title: 'Device registration failed',
    message: ctx.reason ? String(ctx.reason) : 'Please try again.',
  }),
  'device-push-remove-success': () => ({
    type: 'success',
    title: 'Device removed',
    message: 'This device will no longer receive pushes.',
  }),
  'device-push-remove-error': (ctx) => ({
    type: 'error',
    title: 'Could not remove device',
    message: ctx.reason ? String(ctx.reason) : 'Please try again.',
  }),
  'device-push-test-success': () => ({
    type: 'success',
    title: 'Test push sent',
    message: 'Check your device for the notification.',
  }),
  'device-push-test-warning': (ctx) => ({
    type: 'warning',
    title: 'Test push failed',
    message: ctx.reason ? String(ctx.reason) : 'Please try again.',
  }),
  'drive-connected': () => ({
    type: 'success',
    title: 'Google Drive connected',
    message: 'Drive access is ready to use.',
  }),
  'xero-connected': () => ({
    type: 'success',
    title: 'Xero connected',
    message: 'Xero integration is now active.',
  }),
  'xero-connection-failed': (ctx) => ({
    type: 'error',
    title: 'Xero connection failed',
    message: ctx.reason ? String(ctx.reason) : 'Could not connect to Xero.',
  }),
  'xero-disconnected': () => ({
    type: 'success',
    title: 'Xero disconnected',
    message: 'Xero integration turned off.',
  }),
  'xero-disconnect-failed': (ctx) => ({
    type: 'error',
    title: 'Disconnect failed',
    message: ctx.reason ? String(ctx.reason) : 'Could not disconnect from Xero.',
  }),
  'xero-sync-success': (ctx) => ({
    type: 'success',
    title: 'Xero synced',
    message: ctx.count !== undefined ? `Synced ${ctx.count} item(s).` : 'Sync completed.',
  }),
  'xero-sync-error': (ctx) => ({
    type: 'error',
    title: 'Xero sync failed',
    message: ctx.reason ? String(ctx.reason) : 'Could not sync with Xero.',
  }),
};

export function buildNotificationTemplate(id: NotificationTemplateId, ctx: Context = {}): Template {
  return templates[id](ctx);
}
